import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Sparkles, Clock } from 'lucide-react';
import { Button } from './ui/button';

const PartnerskaSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    'Krátké vědomé zastavení jen pro vás dva',
    'Otázky, které běžně nepokládáte',
    'Drobné laskavosti nebo malé překvapení',
    'Fyzickou blízkost bez scénáře',
    'Večer, který nezačne větou „Tak co budem dělat?"',
  ];

  return (
    <section id="partnerska" className="py-20 section-warm">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--vp-pink)]/10 rounded-full text-sm font-medium text-[var(--vp-pink)] mb-6">
              <Heart className="w-4 h-4" />
              Pro páry
            </div>

            <h2 className="text-3xl sm:text-4xl font-semibold text-[var(--vp-ink)] mb-6">
              Partnerská výzva
            </h2>

            <p className="text-lg text-[var(--vp-ink)]/70 mb-4">
              Čtrnáctidenní hra pro dva lidi, kteří už spolu něco mají –
              nebo spolu něco chtějí mít víc.
            </p>

            <p className="text-[var(--vp-ink)]/70 mb-6">
              Každý den vám oběma přijde SMS s novou výzvou. Někdy zabere
              pár minut. Někdy si na ni budete muset udělat večer. Vždy ale
              vytvoří prostor, který běžně v rutinním dni nevznikne.
            </p>

            <div className="bg-white rounded-2xl p-6 border border-[var(--vp-line)] mb-8">
              <p className="text-sm text-[var(--vp-ink)]/60 mb-4">
                Očekávejte:
              </p>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3 text-[var(--vp-ink)]"
                  >
                    <Sparkles className="w-5 h-5 text-[var(--vp-pink)] flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Button
                onClick={() => scrollToSection('#pricing')}
                className="btn-accent px-6 py-3 rounded-full"
              >
                Objednat za 379 Kč
              </Button>
              <div className="flex items-center gap-2 text-sm text-[var(--vp-ink)]/60">
                <Clock className="w-4 h-4" />
                <span>14 dní • 14 společných momentů</span>
              </div>
            </div>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              {/* Main image card */}
              <div className="rounded-3xl overflow-hidden shadow-[var(--shadow-lift)] border border-[var(--vp-line)]">
                <img
                  src="https://images.pexels.com/photos/3171204/pexels-photo-3171204.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Pár užívající společný čas"
                  className="w-full h-80 object-cover"
                />
              </div>

              {/* Floating stats card */}
              <motion.div
                className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-[var(--vp-line)]"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="text-center">
                  <p className="text-2xl font-bold text-[var(--vp-pink)]" style={{ fontFamily: 'var(--font-display)' }}>14</p>
                  <p className="text-xs text-[var(--vp-ink)]/60">dní výzev</p>
                </div>
              </motion.div>

              {/* Progress card */}
              <motion.div
                className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg border border-[var(--vp-line)]"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 5, repeat: Infinity, delay: 0.5 }}
              >
                <p className="text-xs text-[var(--vp-ink)]/60 mb-2">Průběh výzvy</p>
                <div className="flex gap-1">
                  {[...Array(14)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`w-2 h-2 rounded-full ${i < 7 ? 'bg-[var(--vp-mint)]' : 'bg-[var(--vp-line)]'}`} 
                    />
                  ))}
                </div>
              </motion.div>

              {/* Message preview */}
              <motion.div
                className="absolute bottom-8 right-8 bg-[var(--vp-ink)] text-white rounded-2xl p-4 max-w-[200px] shadow-xl"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-xs opacity-80">Denní výzva</span>
                </div>
                <p className="text-sm">
                  Dnes si dejte 10 minut jen pro sebe dva. Žádné telefony.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PartnerskaSection;
